import { Component, Input, Output, EventEmitter, OnInit, OnChanges, ViewChild, TemplateRef } from '@angular/core';
import { EstudianteExamen, MaderaSeleccionada } from '../models/estudiante-examen.model';
import { SharedService } from '../../../services/shared.service';
import { ControlExamenService } from '../../../services/control-examen.service';
import { PreciosExamen } from '../models/precios-config.model';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Graduacion } from '../../../models/graduacion.model';
import { Instructor } from '../../../models/instructor.model';
import { EstudianteService } from 'src/app/services/estudiante.service';
import { Estudiante } from 'src/app/models/estudiante.model';

interface GroupedStudents {
  groupKey: string;
  groupName: string;
  students: EstudianteExamen[];
  count: number;
  instructorData?: Instructor;
  graduacionData?: Graduacion;
}

@Component({
  selector: 'app-tabla-control',
  templateUrl: './tabla-control.component.html',
  styleUrls: ['./tabla-control.component.css']
})
export class TablaControlComponent implements OnInit, OnChanges {
  @Input() estudiantes: EstudianteExamen[] | null = [];
  @Output() estudianteEliminado = new EventEmitter<number>();
  @Output() estudianteEditado = new EventEmitter<EstudianteExamen>();

  @ViewChild('tableHeaderTemplate') tableHeaderTemplate!: TemplateRef<any>;
  @ViewChild('tableRowTemplate') tableRowTemplate!: TemplateRef<any>;

  graduaciones$: Observable<Graduacion[]>;
  instructores$: Observable<Instructor[]>;

  // Observaciones modal properties
  showObservaciones = false;
  estudianteObservaciones: EstudianteExamen | null = null;
  observacionesText = '';

  // Modal state for database views
  showHakkyoDetail = false;
  showItfDetail = false;
  selectedEstudianteForDetail: Estudiante | null = null;
  selectedLibretaForItf: number | null = null;
  isNewEstudiante: boolean = false;

  // Grouping properties
  groupBy: 'none' | 'instructor' | 'graduation' = 'none';
  groupedStudents: GroupedStudents[] = [];
  collapsedGroups: Set<string> = new Set();
  collapsedPaymentSummaries: Set<string> = new Set();

  preciosExamen: PreciosExamen = new PreciosExamen();

  constructor(
    private sharedService: SharedService,
    private controlExamenService: ControlExamenService,
    private estudianteService: EstudianteService
  ) {
    this.graduaciones$ = this.sharedService.graduaciones$;
    this.instructores$ = this.sharedService.instructores$.pipe(
      map(instructores => this.sortInstructores(instructores))
    );
    
    // Subscribe to the pricing configuration
    this.controlExamenService.preciosExamen$.subscribe(
      precios => this.preciosExamen = precios
    );
  }

  ngOnInit(): void {}

  // Handle grouping change
  onGroupByChange(): void {
    this.updateGroupedStudents();
  }

  // Sort the estudiantes array when it changes
  private sortEstudiantes(): void {
    if (this.estudiantes) {
      // Sort by graduacionExamenId first, then by nroLibreta
      this.estudiantes = [...this.estudiantes].sort((a, b) => {
        if (a.idGraduacionExamen !== b.idGraduacionExamen) {
          return a.idGraduacionExamen - b.idGraduacionExamen;
        }
        return a.nroLibreta - b.nroLibreta;
      });
    }
  }

  // Update grouped students when groupBy changes or students change
  private updateGroupedStudents(): void {
    if (!this.estudiantes || this.groupBy === 'none') {
      this.groupedStudents = [];
      return;
    }

    // Get reference data for grouping
    let instructores: Instructor[] = [];
    let graduaciones: Graduacion[] = [];
    
    // Use take(1) to avoid memory leaks
    this.instructores$.pipe(take(1)).subscribe(inst => instructores = inst || []);
    this.graduaciones$.pipe(take(1)).subscribe(grad => graduaciones = grad || []);

    const groups = new Map<string, EstudianteExamen[]>();

    // Group students
    this.estudiantes.forEach(estudiante => {
      let groupKey: string;
      
      if (this.groupBy === 'instructor') {
        groupKey = estudiante.idInstructor.toString();
      } else if (this.groupBy === 'graduation') {
        groupKey = estudiante.idGraduacionExamen.toString();
      } else {
        return;
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(estudiante);
    });

    // Convert to array and sort
    this.groupedStudents = Array.from(groups.entries()).map(([key, students]) => {
      // Create the basic group structure
      const group: GroupedStudents = {
        groupKey: key,
        groupName: '',
        // Sort students by graduacionExamenId first, then by nroLibreta
        students: students.sort((a, b) => {
          if (a.idGraduacionExamen !== b.idGraduacionExamen) {
            return a.idGraduacionExamen - b.idGraduacionExamen;
          }
          return a.nroLibreta - b.nroLibreta;
        }),
        count: students.length
      };
      
      // Add instructor or graduation data for enhanced headers
      if (this.groupBy === 'instructor') {
        const instructorId = parseInt(key);
        const instructor = instructores.find(i => i.id === instructorId);
        group.instructorData = instructor || undefined;
        group.groupName = instructor ? 
          `${instructor.nombre} ${instructor.apellido}${!instructor.activo ? ' - INACTIVO' : ''}` : 
          `Instructor ID: ${key}`;
      } else if (this.groupBy === 'graduation') {
        const graduacionId = parseInt(key);
        if (graduacionId === 0) {
          group.graduacionData = { id: 0, descripcion: 'BLANCO' } as Graduacion;
          group.groupName = 'BLANCO';
        } else {
          const graduacion = graduaciones.find(g => g.id === graduacionId);
          group.graduacionData = graduacion;
          group.groupName = graduacion ? graduacion.descripcion : `Graduación ID: ${key}`;
        }
      }
      
      return group;
    });
    
    // Sort the groups
    if (this.groupBy === 'instructor') {
      // Sort instructors alphabetically by name as before
      this.groupedStudents.sort((a, b) => a.groupName.localeCompare(b.groupName));
    } else if (this.groupBy === 'graduation') {
      // Sort graduations by ID (numerical order)
      this.groupedStudents.sort((a, b) => {
        const idA = parseInt(a.groupKey);
        const idB = parseInt(b.groupKey);
        return idA - idB; // Ascending order
      });
    }
  }

  // Get group name for display
  private getGroupName(groupKey: string, sampleStudent: EstudianteExamen): string {
    if (this.groupBy === 'instructor') {
      return this.getInstructorNombre(parseInt(groupKey), null);
    } else if (this.groupBy === 'graduation') {
      return this.getGraduacionDescripcion(parseInt(groupKey), null);
    }
    return groupKey;
  }

  // Toggle group collapse
  toggleGroup(groupKey: string): void {
    if (this.collapsedGroups.has(groupKey)) {
      this.collapsedGroups.delete(groupKey);
    } else {
      this.collapsedGroups.add(groupKey);
    }
  }

  // Check if group is collapsed
  isGroupCollapsed(groupKey: string): boolean {
    return this.collapsedGroups.has(groupKey);
  }

  // Get students for display (either all or grouped)
  getDisplayStudents(): EstudianteExamen[] | null {
    if (this.groupBy === 'none') {
      return this.estudiantes;
    }
    return null; // Use grouped display instead
  }

  // Sort instructors: active first, then inactive at the end, trigger grouping update
  private sortInstructores(instructores: Instructor[]): Instructor[] {
    const sorted = instructores.sort((a, b) => {
      if (a.activo !== b.activo) {
        return a.activo ? -1 : 1;
      }
      
      if (a.activo && b.activo) {
        return a.id - b.id;
      }
      
      return a.apellido.localeCompare(b.apellido);
    });
    
    // Update grouped students when instructors change
    setTimeout(() => this.updateGroupedStudents(), 0);
    return sorted;
  }

  // Watch for changes to estudiantes input
  ngOnChanges(): void {
    this.sortEstudiantes();
    this.updateGroupedStudents();
  }

  onEliminarEstudiante(nroLibreta: number): void {
    this.estudianteEliminado.emit(nroLibreta);
  }

  onEditarEstudiante(estudiante: EstudianteExamen): void {
    this.estudianteEditado.emit(estudiante);
  }

  onOpenHakkyoDatabase(estudiante: EstudianteExamen): void {
    // Load student data and show modal
    this.estudianteService.getEstudiante(estudiante.nroLibreta).subscribe({
      next: (data) => {
        this.selectedEstudianteForDetail = data;
        this.isNewEstudiante = false;
        this.showHakkyoDetail = true;
      },
      error: (err) => {
        console.log('Student not found, creating new student entry');
        // Create new student object with libreta number and names from exam registration
        this.selectedEstudianteForDetail = new Estudiante(
          estudiante.nroLibreta,
          estudiante.apellido,
          estudiante.nombre
        );
        this.isNewEstudiante = true;
        this.showHakkyoDetail = true;
      }
    });
  }

  onOpenItfDatabase(estudiante: EstudianteExamen): void {
    // Open ITF database modal with libreta number
    this.selectedLibretaForItf = estudiante.nroLibreta;
    this.showItfDetail = true;
  }

  onCloseHakkyoDetail(): void {
    this.showHakkyoDetail = false;
    this.selectedEstudianteForDetail = null;
    this.isNewEstudiante = false;
  }

  onEstudianteSaved(estudianteBD: Estudiante): void {
    // Find the student in the current list and update its DB data
    if (this.estudiantes) {
      const studentToUpdate = this.estudiantes.find(e => e.nroLibreta === estudianteBD.nroLibreta);
      if (studentToUpdate) {
        // Create a copy with updated instructor and other data
        const updatedStudent = Object.assign(
          Object.create(Object.getPrototypeOf(studentToUpdate)), 
          studentToUpdate
        );
        
        // Update instructor if it changed in the database
        // This ensures the grouping by instructor works even if changed in the student detail
        if (estudianteBD.idInstructor) {
          updatedStudent.idInstructor = estudianteBD.idInstructor;
        }
        
        // Update DB reference
        updatedStudent.datosBaseDatos = estudianteBD;
        updatedStudent.existeEnBD = true;
        
        // Trigger update in service to refresh the validation and state
        this.controlExamenService.agregarEstudianteRecepcion(updatedStudent);
      }
    }
    this.onCloseHakkyoDetail();
  }

  onCloseItfDetail(): void {
    this.showItfDetail = false;
    this.selectedLibretaForItf = null;
  }

  getGraduacionDescripcion(id: number, graduaciones: Graduacion[] | null): string {
    if (id === 0) {
      return 'BLANCO';
    }
    
    if (!graduaciones) return `ID: ${id}`;
    
    const graduacion = graduaciones.find(g => g.id === id);
    return graduacion ? graduacion.descripcion : `ID: ${id}`;
  }

  getGraduacionById(id: number, graduaciones: Graduacion[] | null): Graduacion | null {
    if (!graduaciones || id === 0) {
      // Create a mock graduation for BLANCO
      if (id === 0) {
        return { id: 0, descripcion: 'BLANCO' } as Graduacion;
      }
      return null;
    }
    
    return graduaciones.find(g => g.id === id) || null;
  }

  getInstructorNombre(id: number, instructores: Instructor[] | null): string {
    if (!instructores) return `ID: ${id}`;
    
    const instructor = instructores.find(i => i.id === id);
    if (!instructor) return `ID: ${id}`;
    
    // Use the same format as estudiantes-list
    return `${instructor.nombre} ${instructor.apellido}${!instructor.activo ? ' - INACTIVO' : ''}`;
  }

  getStatusClass(estudiante: EstudianteExamen): string {
    if (!estudiante.existeEnBD) return 'status-error';
    if (estudiante.advertencias.length > 0) return 'status-warning';
    if (estudiante.tomoExamen && estudiante.datosCoinciden && estudiante.examenesCompletos) return 'status-success';
    return 'status-pending';
  }

  getRowClass(estudiante: EstudianteExamen): string {
    const baseClass = 'estudiante-row';
    const statusClass = this.getStatusClass(estudiante);
    return `${baseClass} ${statusClass}`;
  }

  getEstudiantesPorEstado(estado: string): EstudianteExamen[] {
    if (!this.estudiantes) return [];
    
    return this.estudiantes.filter(estudiante => {
      const statusClass = this.getStatusClass(estudiante);
      return statusClass === `status-${estado}`;
    });
  }

  getMaderasResumen(maderas: MaderaSeleccionada[]): string {
    return maderas.map(m => `${m.tipo.nombre}x${m.cantidad}`).join(', ');
  }

  getAdvertenciasTooltip(advertencias: string[]): string {
    return advertencias.join('\n');
  }

  formatTime(date: Date): string {
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }

  trackByNroLibreta(index: number, estudiante: EstudianteExamen): number {
    return estudiante.nroLibreta;
  }

  trackByGroup(index: number, group: GroupedStudents): string {
    return group.groupKey;
  }

  // Observaciones methods
  openObservaciones(estudiante: EstudianteExamen): void {
    this.estudianteObservaciones = estudiante;
    this.observacionesText = estudiante.observaciones || '';
    this.showObservaciones = true;
  }

  closeObservaciones(): void {
    this.showObservaciones = false;
    this.estudianteObservaciones = null;
    this.observacionesText = '';
  }

  saveObservaciones(): void {
    if (this.estudianteObservaciones) {
      this.estudianteObservaciones.observaciones = this.observacionesText.trim();
    }
    this.closeObservaciones();
  }

  // Calculate total payments for exams in a group
  getGroupExamTotal(group: GroupedStudents): number {
    return group.students
      .filter(student => student.pago.incluyeExamen)
      .reduce((total, student) => {
        return total + (student.pago.valorExamen ?? (student.pago.incluyeExamen ? this.preciosExamen.examen : 0));
      }, 0);
  }

  // Count exams in group
  getGroupExamCount(group: GroupedStudents): number {
    return group.students.filter(student => student.pago.incluyeExamen).length;
  }

  // Calculate total payments for libretas in a group
  getGroupLibretaTotal(group: GroupedStudents): number {
    return group.students
      .filter(student => student.pago.incluyeLibreta)
      .reduce((total, student) => {
        return total + (student.pago.valorLibreta ?? (student.pago.incluyeLibreta ? this.preciosExamen.libreta : 0));
      }, 0);
  }

  // Count libretas in group
  getGroupLibretaCount(group: GroupedStudents): number {
    return group.students.filter(student => student.pago.incluyeLibreta).length;
  }

  // Calculate total payments for maderas in a group
  getGroupMaderasTotal(group: GroupedStudents): number {
    return group.students.reduce((total, student) => {
      return total + (student.pago.valorMaderas ?? student.pago.maderasSeleccionadas.reduce((maderaTotal, madera) => {
        const maderaTipo = this.preciosExamen.maderas.find(m => m.id === madera.tipo.id);
        const precio = maderaTipo ? maderaTipo.precio : madera.tipo.precio;
        return maderaTotal + (precio * madera.cantidad);
      }, 0));
    }, 0);
  }

  // Count total maderas pieces in group
  getGroupMaderasCount(group: GroupedStudents): number {
    return group.students.reduce((count, student) => {
      const maderasCount = student.pago.maderasSeleccionadas.reduce((maderaCount, madera) => {
        return maderaCount + madera.cantidad;
      }, 0);
      return count + maderasCount;
    }, 0);
  }

  // Calculate total indumentaria payments in a group
  getGroupIndumentariaTotal(group: GroupedStudents): number {
    return group.students.reduce((total, student) => {
      return total + (student.pago.valorIndumentaria ?? student.pago.precioIndumentaria ?? 0);
    }, 0);
  }

  // Calculate total payments for a group
  getGroupPaymentTotal(group: GroupedStudents): number {
    return group.students.reduce((total, student) => {
      return total + student.pago.total;
    }, 0);
  }

  // Calculate total paid amount for a group
  getGroupPaidTotal(group: GroupedStudents): number {
    return group.students
      .filter(student => student.pago.pagado)
      .reduce((total, student) => {
        return total + student.pago.total;
      }, 0);
  }

  // Calculate total pending amount for a group
  getGroupPendingTotal(group: GroupedStudents): number {
    return group.students
      .filter(student => !student.pago.pagado)
      .reduce((total, student) => {
        return total + student.pago.total;
      }, 0);
  }

  // Add method to toggle payment summary collapse
  togglePaymentSummary(groupKey: string): void {
    if (this.collapsedPaymentSummaries.has(groupKey)) {
      this.collapsedPaymentSummaries.delete(groupKey);
    } else {
      this.collapsedPaymentSummaries.add(groupKey);
    }
  }

  // Check if payment summary is collapsed
  isPaymentSummaryCollapsed(groupKey: string): boolean {
    return this.collapsedPaymentSummaries.has(groupKey);
  }

  // Get compact payment summary for collapsed view
  getCompactPaymentSummary(group: GroupedStudents): string {
    const total = this.getGroupPaymentTotal(group);
    const studentCount = group.count;
    const examCount = this.getGroupExamCount(group);
    const libretaCount = this.getGroupLibretaCount(group);
    const maderasCount = this.getGroupMaderasCount(group);
    const indumentariaTotal = this.getGroupIndumentariaTotal(group);
    
    const parts: string[] = [];
    if (examCount > 0) parts.push(`${examCount} exam.`);
    if (libretaCount > 0) parts.push(`${libretaCount} lib.`);
    if (maderasCount > 0) parts.push(`${maderasCount} mad.`);
    if (indumentariaTotal > 0) parts.push('indum.');
    
    return `${studentCount} est. • ${parts.join(' + ')} • $${total.toLocaleString('es-ES')}`;
  }
}